import type { ReactNode } from 'react';
import { AlertCircle, Inbox, Lock, Sparkles } from 'lucide-react';
import { ApiError } from '../../lib/api';
import { cx } from '../../lib/format';
import { Button } from './Button';

export function EmptyState({ icon, title, description, action, className, compact }: { icon?: ReactNode; title: string; description?: ReactNode; action?: ReactNode; className?: string; compact?: boolean }) {
  return (
    <div className={cx('flex flex-col items-center justify-center text-center', compact ? 'px-4 py-8' : 'px-6 py-14', className)}>
      <div className="mb-3 grid size-12 place-items-center rounded-2xl bg-sunken text-ink-3">{icon ?? <Inbox className="size-6" />}</div>
      <h3 className="headline">{title}</h3>
      {description && <p className="mt-1 max-w-[380px] text-[13px] text-ink-2">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Erros da API com tratamento específico para permissão, plano e bloqueio. */
export function ErrorState({ error, onRetry, className }: { error: unknown; onRetry?: () => void; className?: string }) {
  const e = error instanceof ApiError ? error : null;
  if (e?.status === 403 && (e.code === 'FORBIDDEN' || e.code === 'NO_MEMBERSHIP')) {
    return <EmptyState className={className} icon={<Lock className="size-6" />} title="Acesso não permitido" description="Seu papel nesta empresa não inclui este recurso. Fale com o administrador da empresa." />;
  }
  if (e?.status === 402) {
    return <EmptyState className={className} icon={<Sparkles className="size-6" />} title="Recurso fora do plano" description={e.message} />;
  }
  if (e?.code === 'ACCESS_BLOCKED') {
    return <EmptyState className={className} icon={<Lock className="size-6" />} title="Acesso bloqueado" description={e.message} />;
  }
  return (
    <EmptyState
      className={className}
      icon={<AlertCircle className="size-6 text-danger" />}
      title="Não foi possível carregar"
      description={e?.message ?? 'Erro inesperado.'}
      action={onRetry && <Button size="sm" onClick={onRetry}>Tentar novamente</Button>}
    />
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('skeleton', className)} aria-hidden />;
}

export function LoadingRows({ rows = 6 }: { rows?: number }) {
  return (
    <div className="space-y-2.5 p-4" aria-busy="true" aria-label="Carregando">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 w-24" />
        </div>
      ))}
    </div>
  );
}

export function InlineAlert({ tone = 'orange', icon, title, children, action }: { tone?: 'orange' | 'red' | 'blue' | 'green' | 'neutral'; icon?: ReactNode; title?: ReactNode; children?: ReactNode; action?: ReactNode }) {
  const t = {
    orange: 'bg-warning-soft text-warning',
    red: 'bg-danger-soft text-danger',
    blue: 'bg-info-soft text-info',
    green: 'bg-success-soft text-success',
    neutral: 'bg-sunken text-ink-2',
  }[tone];
  return (
    <div className={cx('flex items-start gap-3 rounded-[12px] px-3.5 py-3 text-[13px]', t)} role="status">
      {icon && <div className="mt-0.5 shrink-0">{icon}</div>}
      <div className="min-w-0 flex-1">
        {title && <div className="font-semibold">{title}</div>}
        {children && <div className={cx(!!title && 'mt-0.5', 'text-[13px] opacity-90')}>{children}</div>}
      </div>
      {action}
    </div>
  );
}
